import { Link } from "react-router-dom";
import { useCompany, fmtNipc, Brand } from "../../context/CompanyContext";

/**
 * Termos & Condições — versão pública. O texto base foi redigido para
 * cobrir os requisitos mínimos exigidos pelo direito português / UE para
 * uma plataforma SaaS B2B (gestão de alojamento local). Os campos entre
 * colchetes [] devem ser preenchidos pela entidade titular antes de ir a
 * produção definitiva.
 */
export default function Termos() {
  const { company } = useCompany();
  const updated = "Abril de 2026";
  return (
    <div className="min-h-screen bg-[#041f1b] text-[#fdfbf7]" data-testid="legal-termos-page">
      <main className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-24 space-y-8">
        <header className="space-y-3">
          <Link to="/" className="text-[10px] uppercase tracking-widest text-[#a0b2af] hover:text-[#d4af37]">
            ← Voltar à página inicial
          </Link>
          <div className="eyebrow">Documento legal</div>
          <h1 className="font-display text-4xl sm:text-5xl">Termos &amp; Condições</h1>
          <p className="text-xs text-[#a0b2af]">Última atualização: {updated}</p>
        </header>

        <section className="space-y-3 text-sm leading-relaxed text-[#a0b2af]">
          <h2 className="font-display text-xl text-[#fdfbf7]">1. Identificação do titular</h2>
          <p>
            A {company.trade_name} (doravante &laquo;{company.short_name}&raquo;, &laquo;nós&raquo; ou &laquo;a Plataforma&raquo;)
            é uma plataforma operada por <strong className="text-[#fdfbf7]" data-testid="terms-legal-name">{company.legal_name}</strong>,
            com sede em <strong className="text-[#fdfbf7]">{[company.address, company.postal_code, company.city].filter(Boolean).join(", ")} — {company.country}</strong>
            {company.nipc && <>, NIPC <strong className="text-[#fdfbf7]">{fmtNipc(company.nipc)}</strong></>}, contactável através do email
            {" "}<a href={`mailto:${company.email}`} className="text-[#d4af37] hover:underline">
              {company.email}
            </a>.
          </p>
        </section>

        <section className="space-y-3 text-sm leading-relaxed text-[#a0b2af]">
          <h2 className="font-display text-xl text-[#fdfbf7]">2. Descrição do serviço</h2>
          <p>
            A <Brand short /> disponibiliza uma plataforma de gestão profissional de alojamento local que inclui
            agendamento de limpezas, gestão de equipas, relatórios fotográficos, faturação,
            painel financeiro e notificações automáticas. O serviço é acessível através de browser web e/ou
            aplicação móvel autenticada.
          </p>
        </section>

        <section className="space-y-3 text-sm leading-relaxed text-[#a0b2af]">
          <h2 className="font-display text-xl text-[#fdfbf7]">3. Condições de uso</h2>
          <ul className="list-disc list-inside space-y-1.5">
            <li>O acesso requer a criação de uma conta com dados verdadeiros, completos e atualizados.</li>
            <li>O utilizador é responsável pela confidencialidade das suas credenciais e por toda a atividade realizada na sua conta.</li>
            <li>É proibido utilizar o serviço para fins ilegais, fraudulentos, lesivos de terceiros, ou para tentar comprometer a segurança da Plataforma.</li>
            <li>O utilizador compromete-se a não realizar engenharia reversa, scraping massivo, sobrecarga do sistema ou redistribuição não autorizada do conteúdo.</li>
            <li>O incumprimento destas condições pode resultar em suspensão imediata da conta.</li>
          </ul>
        </section>

        <section className="space-y-3 text-sm leading-relaxed text-[#a0b2af]">
          <h2 className="font-display text-xl text-[#fdfbf7]">4. Limitação de responsabilidade</h2>
          <p>
            A <Brand short /> esforça-se por manter o serviço disponível, seguro e funcional, mas o serviço é
            fornecido &laquo;tal como está&raquo;. Na máxima extensão permitida por lei, a <Brand short /> não
            será responsável por danos indiretos, perda de lucros, perda de dados, interrupção de
            atividade ou outros prejuízos resultantes do uso ou da impossibilidade de uso do serviço,
            exceto em caso de dolo ou negligência grave. A responsabilidade total da <Brand short /> limita-se,
            em qualquer caso, ao valor pago pelo utilizador nos 12 meses anteriores ao facto gerador.
          </p>
        </section>

        <section className="space-y-3 text-sm leading-relaxed text-[#a0b2af]">
          <h2 className="font-display text-xl text-[#fdfbf7]">5. Propriedade intelectual</h2>
          <p>
            Todos os direitos de propriedade intelectual sobre a plataforma, código, design, marcas,
            logótipos e textos pertencem à <Brand short /> ou aos seus licenciadores. O utilizador recebe
            apenas uma licença limitada, pessoal, intransmissível e revogável para utilizar o serviço
            durante a vigência da relação contratual. Os conteúdos carregados pelo utilizador (fotos,
            descrições, dados de reservas) permanecem propriedade do utilizador, que concede à <Brand short />
            uma licença não exclusiva apenas para os fins necessários à prestação do serviço.
          </p>
        </section>

        <section className="space-y-3 text-sm leading-relaxed text-[#a0b2af]">
          <h2 className="font-display text-xl text-[#fdfbf7]">6. Terminação de contas</h2>
          <p>
            O utilizador pode encerrar a sua conta a qualquer momento contactando-nos através do email
            indicado. A <Brand short /> reserva-se o direito de suspender ou eliminar contas em caso de
            violação destes Termos, atividade fraudulenta ou inatividade prolongada superior a 24
            meses. Em caso de terminação, os dados do utilizador serão tratados conforme descrito na
            Política de Proteção de Dados.
          </p>
        </section>

        <section className="space-y-3 text-sm leading-relaxed text-[#a0b2af]">
          <h2 className="font-display text-xl text-[#fdfbf7]">7. Legislação aplicável e jurisdição</h2>
          <p>
            Os presentes Termos regem-se pela lei portuguesa e pelo direito da União Europeia
            aplicável. Qualquer litígio emergente será submetido ao foro da Comarca da Madeira (Funchal),
            com expressa renúncia a qualquer outro, sem prejuízo dos direitos imperativos reconhecidos
            aos consumidores residentes na UE.
          </p>
        </section>

        <section className="space-y-3 text-sm leading-relaxed text-[#a0b2af]">
          <h2 className="font-display text-xl text-[#fdfbf7]">8. Alterações</h2>
          <p>
            Reservamo-nos o direito de atualizar estes Termos. As alterações materiais serão
            comunicadas com aviso prévio razoável (mínimo 15 dias) através da plataforma ou por email.
            A continuação do uso após a entrada em vigor implica a aceitação das alterações.
          </p>
        </section>

        <footer className="border-t border-[#1a4640] pt-6 text-xs text-[#a0b2af] flex flex-wrap items-center justify-between gap-3">
          <span>© {new Date().getFullYear()} · {company.legal_name}</span>
          <Link to="/privacidade" className="text-[#d4af37] hover:underline">
            Ver Política de Proteção de Dados →
          </Link>
        </footer>
      </main>
    </div>
  );
}
